import React, { useRef, useEffect, useState } from 'react';
import YouTube from 'react-youtube';
import { Dialog } from '@headlessui/react';
import { X, Calendar, Eye, FastForward } from 'lucide-react';
import type { Video } from '../services/youtube';
import { loadYouTubeAPI } from '../utils/youtube';

interface VideoPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: Video | null;
}

export function VideoPlayerModal({ isOpen, onClose, video }: VideoPlayerModalProps) {
  const playerRef = useRef<YT.Player | null>(null);
  const [isSpeedModified, setIsSpeedModified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load YouTube API when component mounts or when opened if not loaded
  useEffect(() => {
    if (!window.YT && isOpen) {
      setIsLoading(true);
      loadYouTubeAPI()
        .then(() => {
          setIsAPIReady(true);
          setError(null);
        })
        .catch((err) => {
          console.error('Failed to load YouTube player:', err);
          setError('Failed to load YouTube player. Please try refreshing the page.');
          setIsLoading(false);
        });
    } else if (window.YT && !isAPIReady) {
      setIsAPIReady(true);
    }
  }, [isOpen]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsSpeedModified(false);
      setIsLoading(true);
      setError(null);
    }
  }, [isOpen]);

  // Handle keyboard events for speed modification
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        handleSpeedChange(true);
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        handleSpeedChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isOpen]);

  if (!video) return null;

  const handleSpeedChange = (pressed: boolean) => {
    if (!playerRef.current) return;
    playerRef.current.setPlaybackRate(pressed ? 2 : 1);
    setIsSpeedModified(pressed);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-4xl w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl">
          <div className="relative">
            <button
              onClick={onClose}
              className="absolute right-2 top-2 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div 
              className="aspect-video w-full relative bg-gray-900"
              onMouseDown={() => handleSpeedChange(true)}
              onMouseUp={() => handleSpeedChange(false)}
              onMouseLeave={() => isSpeedModified && handleSpeedChange(false)}
            >
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 border-4 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {error && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-white text-center p-4">
                    <p className="text-red-400 mb-2">Error loading video</p>
                    <p className="text-sm opacity-75">{error}</p>
                  </div>
                </div>
              )}
              {!error && isAPIReady && (
                <YouTube
                  videoId={video.id}
                  opts={{
                    width: '100%',
                    height: '100%',
                    playerVars: {
                      autoplay: 1,
                      modestbranding: 1,
                      rel: 0,
                      origin: window.location.origin,
                    },
                    host: 'https://www.youtube.com',
                  }}
                  className={`w-full h-full transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                  iframeClassName="w-full h-full"
                  onReady={(event: YT.PlayerEvent) => {
                    playerRef.current = event.target;
                    setIsLoading(false);
                  }}
                  onError={(event: YT.PlayerEvent) => {
                    setError('Failed to load video. Please try again.');
                    setIsLoading(false);
                  }}
                  onStateChange={(event: YT.PlayerEvent) => {
                    switch (event.data) {
                      case -1: // unstarted
                        setIsLoading(true);
                        break;
                      case 1: // playing
                        setIsLoading(false);
                        break;
                      case 3: // buffering
                        setIsLoading(true);
                        break;
                    }
                  }}
                />
              )}
              {isSpeedModified && (
                <div className="absolute top-4 left-4 bg-black/75 text-white px-3 py-1.5 rounded-lg flex items-center gap-2">
                  <FastForward className="h-4 w-4" />
                  <span className="text-sm font-medium">2x</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {video.title}
            </h2>
            <p className="text-lg text-indigo-600 dark:text-indigo-400 font-medium mb-4">
              {video.channel}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                <span>{video.views}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{video.publishedAt}</span>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
